import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React, { useContext, useState } from 'react';
import { t } from '../../../../types/translation/Translator';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { Grid } from '@mui/material';
import { TaskContext } from '../../../../context/TaskContext';
import LoadingPackage from '../../Common/LoadingPackage';
import TaskInfoPane from './TaskInfoPane';
import { useMutation } from '@apollo/client';
import {
  DeleteTaskResponse,
  DeleteTaskVariables,
  ProcessTaskResponse,
  ProcessTaskVariables,
  TaskMutations,
} from '../../../../graphql/task.graphql';
import { Task, TaskStatus } from '../../../../types/task';
import { testIds } from '../../../../util/identifiers/identifiers.util';
import { StockLocationRoleAssignmentContext } from '../../../../context/StockLocationRoleAssignmentContext';
import { StockLocationRole } from '../../../../types/stock_location_role_assignment/stockLocationRoleAssignment';
import TaskUsersPane from './TaskUsersPane';
import UpdateTaskAssigneesModal from '../Modals/UpdateTaskAssigneesModal';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import Button from '../../../../VentoryUI/components/common/Button';
import BackButton from '../../../../VentoryUI/components/common/Button/Templates/BackButton';
import DeleteButton from '../../../../VentoryUI/components/common/Button/Templates/DeleteButton';
import Pane from '../../../../VentoryUI/components/common/Pane';
import Paper from '../../../../VentoryUI/components/common/Paper';
import { ColorStyleType } from '../../../../VentoryUI/util/color.util';
import { CompanyContext } from '../../../../context/CompanyContext';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { Routing } from '../../../../util/routes.util';
import { useToast } from '../../../../providers/ToastProvider';
import { ToastType } from '../../../components/Toast';
import { TaskSettingsContext } from '../../../../context/TaskSettingsContext';
import { TaskSettings } from '../../../../types/taskSettings';
import NotFound from '../../../components/NotFound/NotFound';
import LoadingScreen from '../../Common/LoadingScreen';

const tabs = () => [
  {
    text: t(TranslationKey.info).singular.label,
    path: 'info',
    key: 'info',
  },
  {
    text: t(TranslationKey.user).plural.label,
    path: 'users',
    key: 'users',
  },
];

export default function TaskDetailPane() {
  const id = useParams()['id'] || '';
  if (!id) return null;

  const { tasks, setTasks, tasksLoading } = useContext(TaskContext);
  if (tasksLoading) return <LoadingScreen />;
  const task = tasks.get(id);
  if (!task) return <NotFound />;

  return <Element task={task} />;
}

function Element({ task }: { task: Task }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const id = useParams()['id'] || '';

  const { currentCompany } = useContext(CompanyContext);
  const { hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);

  const { tasks, setTasks, tasksLoading } = useContext(TaskContext);
  const { taskSettings } = useContext(TaskSettingsContext);
  const settings = [...taskSettings.values()][0] || new TaskSettings({ companyId: currentCompany.id });

  const taskFt = currentCompany.settings.featureToggles.tasks;
  const hasTaskProcessing = (taskFt.taskOrders.taskOrders && !taskFt.taskOrders.auto) || taskFt.allowStockUpdate;

  const [taskInput, setTaskInput] = useState(new Task(task));
  const [error, setError] = useState<string>('');
  const [updateTaskAssigneesModalOpen, setUpdateTaskAssigneesModalOpen] = useState(false);

  const [remove, { loading: removeLoading }] = useMutation<DeleteTaskResponse, DeleteTaskVariables>(
    TaskMutations.remove,
    {
      onCompleted: res => {
        const task = res.deleteTask[0];
        tasks.delete(task.id);
        setTasks(new Map(tasks));
        navigate(Routing.tasks.overview());
      },
      onError: err => setError(err.message),
    },
  );

  const [process, { loading: processLoading }] = useMutation<ProcessTaskResponse, ProcessTaskVariables>(
    TaskMutations.process,
    {
      onCompleted: res => {
        const task = res.processTask;
        tasks.set(task.id, new Task(task));
        setTasks(tasks);
        if (taskFt.taskOrders.taskOrders) {
          navigate(Routing.operations.orders.overview());
          showToast({
            type: ToastType.success,
            title: t(TranslationKey.orderByTaskCreatedSuccessfullyToastTitle).singular.upper,
          });
        } else {
          navigate(Routing.stock.products.overview());
          showToast({
            type: ToastType.success,
            title: t(TranslationKey.stockUpdatedSuccessfullyToastTitle).singular.upper,
          });
        }
        navigate(Routing.tasks.overview());
      },
      onError: err => setError(err.message),
    },
  );

  const handleDelete = async () => {
    try {
      await remove({
        variables: {
          tasks: [taskInput.forDelete()],
        },
      });
    } catch (e) {
      setError(String(e));
    }
  };

  const handleProcess = async () => {
    try {
      await process({ variables: { taskId: taskInput.id } });
    } catch (e) {
      setError(String(e));
    }
  };

  const loading = removeLoading || processLoading;

  const footer = () => (
    <Grid container>
      {hasStockLocationRole(task.companyId, task.stockLocationId, StockLocationRole.STOCK_LOCATION_MANAGER) ? (
        <>
          <Grid item>
            <DeleteButton onClick={handleDelete} loading={removeLoading} disabled={loading} />
          </Grid>
        </>
      ) : null}

      <Grid item flexGrow={1}>
        <Grid container columnSpacing={1} justifyContent={'flex-end'}>
          {hasStockLocationRole(
            taskInput.companyId,
            taskInput.stockLocationId,
            settings.stockLocationRoleForUpdateStock,
          ) ? (
            <>
              {task.status === TaskStatus.complete && hasTaskProcessing ? (
                <Grid item>
                  <Button
                    style={ColorStyleType.secondary}
                    text={(() => {
                      if (taskFt.taskOrders.taskOrders) return t(TranslationKey.createOrder).singular.upper;
                      if (taskFt.allowStockUpdate) return t(TranslationKey.updateStock).singular.upper;
                      return t(TranslationKey.process).singular.upper;
                    })()}
                    onClick={handleProcess}
                    loading={processLoading}
                    disabled={loading}
                    startIcon={<NoteAddIcon />}
                  />
                </Grid>
              ) : null}
              <Grid item>
                <Button
                  disabled={loading}
                  onClick={() => setUpdateTaskAssigneesModalOpen(true)}
                  testId={testIds.assignUsers}
                  text={t(TranslationKey.reassignUsers).singular.label}
                  startIcon={<GroupAddIcon />}
                />
              </Grid>
            </>
          ) : null}
          <Grid item>
            <BackButton onClick={() => navigate(Routing.tasks.overview())} disabled={loading} style='secondary' />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <>
      <UpdateTaskAssigneesModal
        task={taskInput}
        setTask={setTaskInput}
        open={updateTaskAssigneesModalOpen}
        setOpen={setUpdateTaskAssigneesModalOpen}
      />

      <Pane error={error} tabs={tabs()} testId={testIds.taskDetailPane}>
        {tasksLoading && !tasks.get(id) ? (
          <Paper>
            <Grid container alignItems={'center'}>
              <Grid item className='fill-gray-300 ' marginX={'auto'}>
                <LoadingPackage />
              </Grid>
            </Grid>
          </Paper>
        ) : (
          <Routes>
            <Route path='info' element={<TaskInfoPane task={taskInput} footer={footer} />} />
            <Route path='users' element={<TaskUsersPane task={taskInput} footer={footer} />} />
          </Routes>
        )}
      </Pane>
    </>
  );
}
